/**
 * Mock data for Disputes Management
 */

import type { Dispute, DisputeStatus } from '../../types/dispute';
import { generateId } from './mockData';
import { mockMissions } from './mockMission';

const statuses: DisputeStatus[] = ['underreview', 'resolved', 'rejected'];

const clients = [
  'A. Dane',
  'J. Smith',
  'M. Garcia',
  'B. Williams',
  'T. Jones',
  'S. Johnson',
  'R. Brown',
  'L. Davis',
];

const agents = [
  'S. Miller',
  'R. Chen',
  'L. Kim',
  'P. Wilson',
  'K. Martinez',
  'D. Anderson',
  'J. Taylor',
];

// formatDate helper removed as currently unused

/**
 * Get mission title by missionId
 * Tries to find mission by id or code, returns a default title if not found
 */
const getMissionTitle = (missionId: string): string => {
  // Try to find mission by id or code
  const mission = mockMissions.find(
    (m) => m.id === missionId || m.code === missionId
  );

  if (mission) {
    return mission.title;
  }

  // Generate a default title based on missionId if not found
  // Extract number from missionId (e.g., "MSN-0289" -> "0289")
  const match = missionId.match(/\d+/);
  if (match) {
    const missionNum = match[0];
    const missionTypes = ['Quick Hit', 'Light Surveillance', 'Full Investigative'];
    const randomType = missionTypes[Math.floor(Math.random() * missionTypes.length)];
    return `${randomType} Mission ${missionNum}`;
  }

  return 'Unknown Mission';
};

// Generate mock disputes matching the image
const generateMockDisputes = (): Dispute[] => {
  const disputes: Dispute[] = [];
  const now = new Date();

  // Predefined disputes matching the image
  const predefinedDisputes: Dispute[] = [
    // Dashboard Case # mappings
    {
      id: generateId(),
      disputeId: 'D-00289',
      missionId: 'MSN-0289',
      missionTitle: getMissionTitle('MSN-0289'),
      status: 'underreview',
      dateOpened: new Date(now.getTime() - 3 * 60 * 60 * 1000), // 3 hours ago
      lastUpdate: new Date(now.getTime() - 3 * 60 * 60 * 1000),
      parties: [
        { type: 'client', name: 'J. Smith', email: 'j.smith@example.com' },
        { type: 'agent', name: 'R. Chen', email: 'r.chen@example.com' },
      ],
    },
    {
      id: generateId(),
      disputeId: 'D-00156',
      missionId: 'MSN-0156',
      missionTitle: getMissionTitle('MSN-0156'),
      status: 'underreview',
      dateOpened: new Date(now.getTime() - 6 * 60 * 60 * 1000), // 6 hours ago
      lastUpdate: new Date(now.getTime() - 6 * 60 * 60 * 1000),
      parties: [
        { type: 'client', name: 'M. Garcia', email: 'm.garcia@example.com' },
        { type: 'agent', name: 'L. Kim', email: 'l.kim@example.com' },
      ],
    },
    {
      id: generateId(),
      disputeId: 'D-84210',
      missionId: 'MSN-99427',
      missionTitle: getMissionTitle('MSN-99427'),
      status: 'underreview',
      dateOpened: new Date('2024-07-21'),
      lastUpdate: new Date('2024-07-22'),
      parties: [
        { type: 'client', name: 'A. Dane', email: 'a.dane@example.com' },
        { type: 'agent', name: 'S. Miller', email: 's.miller@example.com' },
      ],
    },
    {
      id: generateId(),
      disputeId: 'D-84198',
      missionId: 'MSN-99415',
      missionTitle: getMissionTitle('MSN-99415'),
      status: 'underreview',
      dateOpened: new Date('2024-07-20'),
      lastUpdate: new Date('2024-07-20'),
      parties: [
        { type: 'client', name: 'J. Smith', email: 'j.smith@example.com' },
        { type: 'agent', name: 'R. Chen', email: 'r.chen@example.com' },
      ],
    },
    {
      id: generateId(),
      disputeId: 'D-83112',
      missionId: 'MSN-98201',
      missionTitle: getMissionTitle('MSN-98201'),
      status: 'resolved',
      dateOpened: new Date('2024-07-15'),
      lastUpdate: new Date('2024-07-18'),
      parties: [
        { type: 'client', name: 'M. Garcia', email: 'm.garcia@example.com' },
        { type: 'agent', name: 'L. Kim', email: 'l.kim@example.com' },
      ],
    },
    {
      id: generateId(),
      disputeId: 'D-83056',
      missionId: 'MSN-98114',
      missionTitle: getMissionTitle('MSN-98114'),
      status: 'rejected',
      dateOpened: new Date('2024-07-14'),
      lastUpdate: new Date('2024-07-16'),
      parties: [
        { type: 'client', name: 'B. Williams', email: 'b.williams@example.com' },
        { type: 'agent', name: 'S. Miller', email: 's.miller@example.com' },
      ],
    },
    {
      id: generateId(),
      disputeId: 'D-82998',
      missionId: 'MSN-97991',
      missionTitle: getMissionTitle('MSN-97991'),
      status: 'resolved',
      dateOpened: new Date('2024-07-12'),
      lastUpdate: new Date('2024-07-15'),
      parties: [
        { type: 'client', name: 'T. Jones', email: 't.jones@example.com' },
        { type: 'agent', name: 'R. Chen', email: 'r.chen@example.com' },
      ],
    },
  ];

  // Generate additional disputes
  for (let i = 0; i < 95; i++) {
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    const dateOpened = new Date(now.getTime() - (i + 1) * 24 * 60 * 60 * 1000);
    const lastUpdate = new Date(dateOpened.getTime() + Math.random() * 5 * 24 * 60 * 60 * 1000);
    const disputeNumber = Math.floor(Math.random() * 90000) + 10000;
    const missionNumber = Math.floor(Math.random() * 90000) + 10000;
    const disputeId = `D-${disputeNumber}`;
    const missionId = `MSN-${missionNumber}`;

    disputes.push({
      id: generateId(),
      disputeId,
      missionId,
      missionTitle: getMissionTitle(missionId),
      status,
      dateOpened,
      lastUpdate,
      parties: [
        {
          type: 'client',
          name: clients[Math.floor(Math.random() * clients.length)],
          email: `${clients[Math.floor(Math.random() * clients.length)].toLowerCase().replace('. ', '')}@example.com`
        },
        {
          type: 'agent',
          name: agents[Math.floor(Math.random() * agents.length)],
          email: `${agents[Math.floor(Math.random() * agents.length)].toLowerCase().replace('. ', '')}@example.com`
        },
      ],
    });
  }

  return [...predefinedDisputes, ...disputes];
};

export const mockDisputes: Dispute[] = generateMockDisputes();

